import { AppBar, Box, IconButton, Stack, type SxProps } from "@mui/material";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useRef, useState, type FC, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Menu } from "../../../styles/icons/menu";
import { sidebarOpenAtom, toggleSidebarAtom } from "../../layout/components/sidebar/store";
import { layoutAtom } from "../../layout/components/store";

interface HeaderBarProps {
  navBurger?: boolean;
  sx?: SxProps;
  children?: ReactNode;
}

export const HeaderBar: FC<HeaderBarProps> = ({ navBurger, children, sx = [] }) => {
  const { t } = useTranslation();

  const layout = useAtomValue(layoutAtom);
  const toggleSidebar = useSetAtom(toggleSidebarAtom);
  const sidebarOpen = useAtomValue(sidebarOpenAtom);

  const appBarRef = useRef<HTMLDivElement | null>(null);
  const [appBarHeight, setAppBarHeight] = useState(0);

  const measure = useCallback(() => {
    const el = appBarRef.current;
    if (!el) return;
    const h = el.offsetHeight;
    setAppBarHeight(h);
  }, []);

  useEffect(() => {
    const raf = requestAnimationFrame(measure);

    const el = appBarRef.current;
    let ro: ResizeObserver | undefined;
    if (el && "ResizeObserver" in window) {
      ro = new ResizeObserver(() => measure());
      ro.observe(el);
    }

    const vv = window.visualViewport;
    vv?.addEventListener("resize", measure);
    vv?.addEventListener("scroll", measure);

    // Fallback: window resize
    window.addEventListener("resize", measure);

    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      vv?.removeEventListener("resize", measure);
      vv?.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  return (
    <>
      <AppBar
        ref={appBarRef}
        position="fixed"
        color="inherit"
        sx={{
          top: 0,
          right: 0,
          px: 2,
          py: 1,
          borderWidth: 1,
          borderColor: "grey.200",
          borderStyle: "solid",
          background: "background.paper",
          zIndex: 1100,
          ...sx,
        }}
        role="heading"
      >
        <Stack direction="row" alignItems="center" sx={{ flexGrow: 1 }} spacing={2}>
          {navBurger && (
            <IconButton
              data-analytics-id="sidebar-open"
              sx={(theme) => ({
                color: layout === "default" ? theme.palette.text.primary : "var(--ah-text-color)",
                boxShadow: sidebarOpen
                  ? `0 0 5px 5px ${theme.palette.text.primary}aa`
                  : `0 0 0 0  ${theme.palette.text.primary}00`,
                transition: "box-shadow 2s ease-out",
              })}
              onClick={toggleSidebar}
              title={t("Open main nav")}
            >
              <Menu />
            </IconButton>
          )}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ flexGrow: 1 }}
          >
            {children}
          </Stack>
        </Stack>
      </AppBar>

      {/* Spacer dynamically matches AppBar height */}
      <Box sx={{ height: appBarHeight }} />
    </>
  );
};
